import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { SortOption } from "@/components/search/ActiveSearchTabs";
import { CarrierOffersSection } from "@/components/search/CarrierOffersSection";

import { SearchFilterBar, type SearchFilterParams } from "@/components/search/SearchFilterBar";
import { MobileSearchSummary } from "@/components/search/MobileSearchSummary";
import { SearchResultsTable } from "@/components/search/SearchResultsTable";
import { SimilarResultsSection } from "@/components/search/SimilarResultsSection";
import { FeaturedLoadsRail } from "@/components/search/FeaturedLoadsRail";
import { usePageMeta } from "@/hooks/usePageMeta";
import { searchLoads, searchSimilarLoads, searchStateToStateSameEquipment, searchStateToStateAnyEquipment, parseStateFromInput, geocodeLocation, expandEquipmentTypesForFallback, hasSpecificSubtype, matchesSearchFilters, clampRadiusValue, type DatabaseLoad, type LoadSearchParams, type GeoCoords } from "@/lib/loadSearch";
import { useBoostedLoads } from "@/hooks/useBoostedLoads";
import {
  ActiveSearchTabs,
  createBlankTab,
  DEFAULT_FILTERS,
  type SearchTab,
  type SearchTabFilters,
} from "@/components/search/ActiveSearchTabs";
import { useLoadBoardRealtime, FADE_REMOVAL_DELAY_MS } from "@/hooks/useLoadBoardRealtime";
import { useSearchAlerts } from "@/hooks/useSearchAlerts";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/* ─── localStorage helpers ─────────────────────────────────────────── */
const STORAGE_KEY = "lr-search-tabs";

function loadTabsFromStorage(): { tabs: SearchTab[]; activeTabId: string } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed.tabs) && parsed.tabs.length > 0) {
        return { tabs: parsed.tabs, activeTabId: parsed.activeTabId || parsed.tabs[0].id };
      }
    }
  } catch {
    // ignore
  }
  return { tabs: [], activeTabId: "" };
}

function saveTabsToStorage(tabs: SearchTab[], activeTabId: string) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ tabs, activeTabId }));
  } catch {
    // ignore
  }
}

/* ─── Client-side post-filters ─────────────────────────────────────── */
interface ClientFilters {
  minWeight: number | null;
  minLength: number | null;
  pickupDateFrom: string | null;
  pickupDateTo: string | null;
}

function applyClientFilters(loads: DatabaseLoad[], filters: ClientFilters): DatabaseLoad[] {
  return loads.filter((load) => {
    if (filters.minWeight && (load.weight_lbs ?? 0) < filters.minWeight) return false;
    if (filters.minLength && (load.length_ft ?? 0) < filters.minLength) return false;
    if (filters.pickupDateFrom && load.pickup_date < filters.pickupDateFrom) return false;
    if (filters.pickupDateTo && load.pickup_date > filters.pickupDateTo) return false;
    return true;
  });
}

/* ─── Build tab label from filters ─────────────────────────────────── */
function buildLabel(filters: SearchTabFilters): string {
  const o = filters.origin?.trim();
  const d = filters.destination?.trim();
  if (o && d) return `${o} → ${d}`;
  if (o) return `${o} → Anywhere`;
  if (d) return `Anywhere → ${d}`;
  return "New Search";
}

/* How long the green "new" highlight persists (ms) */
const NEW_LOAD_HIGHLIGHT_MS = 30_000;

/* ─── Convert stored tab filters → SearchFilterParams for handleSearch ── */
function tabFiltersToSearchParams(f: SearchTabFilters): SearchFilterParams {
  return {
    origin: f.origin ?? "",
    destination: f.destination ?? "",
    dhoRadius: clampRadiusValue(f.dhoRadius, 50),
    dhdRadius: clampRadiusValue(f.dhdRadius, 50),
    equipmentTypes: f.equipmentTypes ?? [],
    loadType: (f.loadType as "full" | "partial" | "both") ?? "both",
    minWeight: f.minWeight ? Number(f.minWeight) : null,
    minLength: f.minLength ? Number(f.minLength) : null,
    pickupDateFrom: f.pickupDate ?? null,
    pickupDateTo: null,
    hideCanada: f.hideCanada ?? false,
    searchBack: f.searchBack ? Number(f.searchBack) : 3,
  };
}

/* ═══════════════════════════════════════════════════════════════════ */
export default function SearchLoadsPageV2() {
  usePageMeta({
    title: "Search Loads | LaneRunner",
    description: "Find freight loads on LaneRunner",
  });

  // ── Alert system ──
  const { fireAlert, requestPermission, clearAlerted } = useSearchAlerts();

  // ── Auth ──
  const { profile, isDemoUser } = useAuth();

  // ── Tab state (persisted to localStorage) ──
  const [tabs, setTabs] = useState<SearchTab[]>(() => loadTabsFromStorage().tabs);
  const [activeTabId, setActiveTabId] = useState<string>(() => loadTabsFromStorage().activeTabId);

  // ── Mobile-only: per-tab collapsed state for the search form ──
  const [formCollapsedMap, setFormCollapsedMap] = useState<Record<string, boolean>>({});

  // ── Pending filters for when no tabs exist ──
  const pendingFiltersRef = useRef<SearchTabFilters>({ ...DEFAULT_FILTERS, equipmentTypes: [] });

  // ── In-memory result cache (not persisted) ──
  const resultsCache = useRef<Map<string, DatabaseLoad[]>>(new Map());

  // ── Coords cache: tabId → { originCoords, destCoords } ──
  const coordsCache = useRef<Map<string, { originCoords: GeoCoords | null; destCoords: GeoCoords | null }>>(new Map());

  // ── Search params cache: tabId → LoadSearchParams ──
  const searchParamsCache = useRef<Map<string, LoadSearchParams>>(new Map());

  // ── Per-tab loading state ──
  const [searchingTabs, setSearchingTabs] = useState<Record<string, boolean>>({});
  const [similarLoadingTabs, setSimilarLoadingTabs] = useState<Record<string, boolean>>({});
  // Background refresh state — separate from foreground searchingTabs so the table
  // does NOT switch to its loading skeleton on stale-tab refresh.
  const [refreshingTabs, setRefreshingTabs] = useState<Record<string, boolean>>({});
  // Per-tab last successful fetch timestamp (only set on successful results write).
  const lastFetchedAt = useRef<Map<string, number>>(new Map());
  const searchGenByTab = useRef<Map<string, number>>(new Map()); // per-tab generation counter
  const abortByTab = useRef<Map<string, AbortController>>(new Map()); // per-tab abort controller
  // Guard: tracks tabs that have been auto-searched for current activation path.
  // Cleared on tab close and when filters change (manual search path).
  const autoSearchedTabs = useRef<Set<string>>(new Set());
  // Derived: active tab loading state (drives all UI)
  const isSearching = activeTabId ? !!searchingTabs[activeTabId] : false;
  const isSimilarLoading = activeTabId ? !!similarLoadingTabs[activeTabId] : false;
  const isRefreshing = activeTabId ? !!refreshingTabs[activeTabId] : false;

  // ── Similar results caches ──
  const similarResultsCache = useRef<Map<string, DatabaseLoad[]>>(new Map());
  const similarCountCache = useRef<Map<string, number>>(new Map());
  // Per-tab metadata for which fallback layers contributed (for subtitle rendering)
  type SimilarMeta = {
    sources: Array<'layer1' | 'layer2' | 'layer3'>;
    originState: string | null;
    destState: string | null;
  };
  const similarMetaCache = useRef<Map<string, SimilarMeta>>(new Map());

  // ── Realtime state ──
  const [fadingLoads, setFadingLoads] = useState<Map<string, string>>(new Map()); // loadId → status
  const [newLoadIds, setNewLoadIds] = useState<Set<string>>(new Set());
  const [realtimeTick, setRealtimeTick] = useState(0);

  // ── Per-tab unseen counter (page-only, in-memory) ──
  const [tabUnseen, setTabUnseen] = useState<Record<string, number>>({});
  // Dedup so the same load can't be counted twice for the same tab
  // (e.g. realtime INSERT then again via background refresh).
  const unseenSeenIds = useRef<Map<string, Set<string>>>(new Map());
  const markUnseenSeen = (tabId: string, loadId: string): boolean => {
    let set = unseenSeenIds.current.get(tabId);
    if (!set) { set = new Set(); unseenSeenIds.current.set(tabId, set); }
    if (set.has(loadId)) return false;
    set.add(loadId);
    // FIFO cap to avoid unbounded growth per tab
    if (set.size > 500) {
      const first = set.values().next().value;
      if (first) set.delete(first);
    }
    return true;
  };

  // ── Deep-link hydration from URL query params (one-time) ──
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkConsumed = useRef(false);
  const showMyOffers = searchParams.get("tab") === "my-offers";

  useEffect(() => {
    if (deepLinkConsumed.current) return;
    if (showMyOffers) { deepLinkConsumed.current = true; return; }
    const origin = searchParams.get("origin") || "";
    const destination = searchParams.get("destination") || "";
    const equipment = searchParams.get("equipment") || "";
    const source = searchParams.get("source") || "";

    if (!origin && !destination) return;
    deepLinkConsumed.current = true;

    const filters: SearchTabFilters = {
      ...DEFAULT_FILTERS,
      origin,
      destination,
      equipmentTypes: equipment ? [equipment] : [],
    };

    // Check if an existing tab already matches these filters
    const existingTab = tabs.find(
      (t) =>
        t.filters.origin === origin &&
        t.filters.destination === destination &&
        JSON.stringify(t.filters.equipmentTypes) === JSON.stringify(filters.equipmentTypes)
    );

    if (existingTab) {
      setActiveTabId(existingTab.id);
    } else {
      const newTab: SearchTab = {
        id: crypto.randomUUID(),
        label: buildLabel(filters),
        filters,
        hasSearched: false,
      };
      // Hard cap: max 8 open Search Loads tabs
      let added = false;
      setTabs((prev) => {
        if (prev.length >= 8) {
          toast("Tab limit reached", {
            description: "You can have up to 8 Search Loads tabs open. Close one to open another.",
            duration: 5000,
          });
          return prev;
        }
        added = true;
        return [...prev, newTab];
      });
      if (added) setActiveTabId(newTab.id);
    }

    // Clear URL params so refresh doesn't replay
    setSearchParams({}, { replace: true });

    if (source) {
      console.log(`[Search] Deep-link from: ${source}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist tabs to localStorage on change
  useEffect(() => {
    saveTabsToStorage(tabs, activeTabId);
  }, [tabs, activeTabId]);

  // ── Derived: current tab (may be undefined) ──
  const activeTab = tabs.find((t) => t.id === activeTabId);
  const cachedResults = activeTab ? resultsCache.current.get(activeTab.id) : undefined;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _tick = realtimeTick; // force re-render on tick

  // ── Current filters for the filter bar ──
  const currentFilters = activeTab?.filters ?? pendingFiltersRef.current;
  const filterBarKey = activeTabId || "__no-tab__";

  // ── Auto-search on tab switch when cache is missing ──
  useEffect(() => {
    if (!activeTab || !activeTab.hasSearched) return;
    if (resultsCache.current.has(activeTab.id)) return;
    // Guard: only auto-search once per activation (prevents re-trigger loops)
    if (autoSearchedTabs.current.has(activeTab.id)) return;
    autoSearchedTabs.current.add(activeTab.id);

    // Cache is missing for a previously-searched tab → auto-run
    const params = tabFiltersToSearchParams(activeTab.filters);
    handleSearch(params);
  }, [activeTabId]); // only fires on tab switch, not on loading state changes

  // ── On mount: rehydrate searchParams + coords caches for previously-searched tabs ──
  // Both caches are useRef Maps and reset on unmount. Without this, leaving and
  // returning to /search would silently disable inactive-tab realtime matching
  // until the user clicked each tab once. Page-only; no DB search; geocode is
  // local-first and cached.
  useEffect(() => {
    let cancelled = false;
    const targets = tabs.filter(
      (t) => t.hasSearched && !searchParamsCache.current.has(t.id)
    );
    if (targets.length === 0) return;

    // Synchronously seed search params so matching works even before geocode resolves.
    for (const t of targets) {
      const p = tabFiltersToSearchParams(t.filters);
      const sp: LoadSearchParams = {
        origin: p.origin,
        destination: p.destination,
        equipmentTypes: p.equipmentTypes,
        loadType: p.loadType as 'full' | 'partial' | 'both',
        hideCanada: p.hideCanada,
        searchBack: p.searchBack,
        dhoRadius: p.dhoRadius,
        dhdRadius: p.dhdRadius,
      };
      searchParamsCache.current.set(t.id, sp);
    }

    // Resolve coords in parallel; cached/local-first.
    (async () => {
      await Promise.all(
        targets.map(async (t) => {
          try {
            const [originCoords, destCoords] = await Promise.all([
              t.filters.origin ? geocodeLocation(t.filters.origin) : Promise.resolve(null),
              t.filters.destination ? geocodeLocation(t.filters.destination) : Promise.resolve(null),
            ]);
            if (cancelled) return;
            coordsCache.current.set(t.id, { originCoords, destCoords });
          } catch {
            if (cancelled) return;
            coordsCache.current.set(t.id, { originCoords: null, destCoords: null });
          }
        })
      );
    })();

    return () => { cancelled = true; };
    // Mount-only; intentional empty deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Stale-tab background refresh on tab switch ──
  // If we already have cached results for the active tab but they are older than
  // STALE_THRESHOLD_MS, kick off a background refresh (no foreground spinner).
  // Debounced 250ms so quick tab-flipping does not fire requests.
  useEffect(() => {
    const STALE_THRESHOLD_MS = 60_000;
    const DEBOUNCE_MS = 250;
    if (!activeTab || !activeTab.hasSearched) return;
    if (!resultsCache.current.has(activeTab.id)) return; // cold path handled above
    const last = lastFetchedAt.current.get(activeTab.id) ?? 0;
    if (Date.now() - last < STALE_THRESHOLD_MS) return;
    if (searchingTabs[activeTab.id] || refreshingTabs[activeTab.id]) return;

    const tabId = activeTab.id;
    const params = tabFiltersToSearchParams(activeTab.filters);
    const t = setTimeout(() => {
      // Re-check active tab + in-flight state at fire time to stay safe under
      // fast switching (the per-tab abort/gen plumbing handles the rest).
      if (activeTabIdRef.current !== tabId) return;
      if (searchingTabs[tabId] || refreshingTabs[tabId]) return;
      handleSearch(params, { background: true });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);

  // ── Visible load IDs for realtime matching ──
  const visibleLoadIds = useMemo(() => {
    const ids = new Set<string>();
    if (cachedResults) {
      for (const l of cachedResults) ids.add(l.id);
    }
    return ids;
  }, [cachedResults]);

  // ── Active search params for realtime ──
  const activeSearchParams = activeTab ? searchParamsCache.current.get(activeTab.id) ?? null : null;
  const activeCoords = activeTab ? coordsCache.current.get(activeTab.id) ?? null : null;

  // ── Update a specific tab ──
  const updateTab = useCallback((tabId: string, patch: Partial<SearchTab>) => {
    setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, ...patch } : t)));
  }, []);

  // ── Add tab with hard cap (max 8) ──
  const addTabWithOverflow = useCallback(
    (newTab: SearchTab): boolean => {
      let added = false;
      setTabs((prev) => {
        if (prev.length >= 8) {
          toast("Tab limit reached", {
            description: "You can have up to 8 Search Loads tabs open. Close one to open another.",
            duration: 5000,
          });
          return prev;
        }
        added = true;
        return [...prev, newTab];
      });
      if (added) setActiveTabId(newTab.id);
      return added;
    },
    [],
  );

  // ── Realtime callbacks ──
  // The hook now forwards ALL posted-load INSERTs (demo-isolated). This handler
  // decides per-tab what to do:
  //   (a) Active-tab path: if the load matches the active tab's filters, prepend
  //       to results, highlight, optionally chime, and toast.
  //   (b) Inactive-tab fan-out: for every other searched tab whose filters
  //       match, increment its unseen badge (deduped via markUnseenSeen).
  const handleNewLoad = useCallback((load: DatabaseLoad) => {
    // ── (a) Active-tab path ──
    if (activeTabId) {
      const activeSp = searchParamsCache.current.get(activeTabId);
      const activeC = coordsCache.current.get(activeTabId);
      const current = resultsCache.current.get(activeTabId);

      if (
        activeSp &&
        current &&
        !current.some(l => l.id === load.id) &&
        matchesSearchFilters(load, activeSp, activeC?.originCoords ?? null, activeC?.destCoords ?? null)
      ) {
        // Prepend
        resultsCache.current.set(activeTabId, [load, ...current]);
        setNewLoadIds(prev => new Set(prev).add(load.id));
        setRealtimeTick(t => t + 1);

        // Mark seen for active tab so a later background path doesn't double-count
        markUnseenSeen(activeTabId, load.id);

        // Fire alert + toast ONLY if alerts are enabled for the active tab.
        // Without this gate, every matching new posted load fired a toast,
        // which felt like spam (especially for carriers with broad searches).
        setTabs(prev => {
          const tab = prev.find(t => t.id === activeTabId);
          if (tab?.alertEnabled) {
            fireAlert(load);

            const lane = `${load.origin_city}, ${load.origin_state} → ${load.destination_city}, ${load.destination_state}`;
            const details = `${load.equipment_type || 'Van'}${load.rate_amount ? ` · $${load.rate_amount.toLocaleString()}` : ''}`;
            toast(lane, {
              description: details,
              action: {
                label: 'View Load',
                onClick: () => {
                  const row = document.getElementById(`load-row-${load.id}`);
                  if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
                },
              },
              duration: 8000,
              position: 'bottom-right',
            });
          }
          return prev; // no mutation
        });

        // Remove "new" highlight after delay
        setTimeout(() => {
          setNewLoadIds(prev => {
            const next = new Set(prev);
            next.delete(load.id);
            return next;
          });
        }, NEW_LOAD_HIGHLIGHT_MS);
      }
    }

    // ── (b) Inactive-tab fan-out: increment unseen badge + chime for matches ──
    const otherTabs = tabsRef.current.filter(t => t.id !== activeTabId && t.hasSearched);
    if (otherTabs.length > 0) {
      const increments: Record<string, number> = {};
      let anyAlertEnabled = false;
      let anyMatch = false;
      for (const t of otherTabs) {
        const sp = searchParamsCache.current.get(t.id);
        if (!sp) continue;
        const c = coordsCache.current.get(t.id);
        if (matchesSearchFilters(load, sp, c?.originCoords ?? null, c?.destCoords ?? null)) {
          if (markUnseenSeen(t.id, load.id)) {
            increments[t.id] = (increments[t.id] ?? 0) + 1;
            anyMatch = true;
            if (t.alertEnabled) anyAlertEnabled = true;
          }
        }
      }
      if (Object.keys(increments).length > 0) {
        setTabUnseen(prev => {
          const next = { ...prev };
          for (const [id, n] of Object.entries(increments)) {
            next[id] = (next[id] ?? 0) + n;
          }
          return next;
        });
      }
      // Chime once per load for inactive matches, but only when at least
      // one matched tab has alerts enabled. Bell toggle is authoritative.
      if (anyMatch && anyAlertEnabled) {
        fireAlert(load);
      }
    }
  }, [activeTabId, fireAlert]);

  const handleFadeLoad = useCallback((loadId: string, newStatus: string) => {
    setFadingLoads(prev => new Map(prev).set(loadId, newStatus));

    // After delay, remove from results
    setTimeout(() => {
      if (!activeTabId) return;
      const current = resultsCache.current.get(activeTabId);
      if (current) {
        resultsCache.current.set(activeTabId, current.filter(l => l.id !== loadId));
      }
      setFadingLoads(prev => {
        const next = new Map(prev);
        next.delete(loadId);
        return next;
      });
      setRealtimeTick(t => t + 1);
    }, FADE_REMOVAL_DELAY_MS);
  }, [activeTabId]);

  // ── Realtime subscription ──
  useLoadBoardRealtime({
    enabled: !!activeTab?.hasSearched,
    searchParams: activeSearchParams,
    originCoords: activeCoords?.originCoords ?? null,
    destCoords: activeCoords?.destCoords ?? null,
    visibleLoadIds,
    isDemoUser,
    onNewLoad: handleNewLoad,
    onFadeLoad: handleFadeLoad,
  });

  // ── Stable ref for activeTabId to avoid callback churn ──
  const activeTabIdRef = useRef(activeTabId);
  activeTabIdRef.current = activeTabId;

  // ── Stable ref for tabs to read current state in callbacks ──
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  // ── Filter change from SearchFilterBar ──
  const handleFilterChange = useCallback(
    (filters: SearchTabFilters) => {
      const tabId = activeTabIdRef.current;
      if (tabId) {
        updateTab(tabId, { filters });
      } else {
        pendingFiltersRef.current = filters;
      }
    },
    [updateTab],
  );

  // ── Helper: set per-tab searching state ──
  const setTabSearching = useCallback((tabId: string, val: boolean) => {
    setSearchingTabs(prev => ({ ...prev, [tabId]: val }));
  }, []);
  const setTabSimilarLoading = useCallback((tabId: string, val: boolean) => {
    setSimilarLoadingTabs(prev => ({ ...prev, [tabId]: val }));
  }, []);

  // ── Search ──
  const handleSearch = useCallback(
    async (params: SearchFilterParams, opts?: { background?: boolean }) => {
      const isBackground = !!opts?.background;
      const newFilters: SearchTabFilters = {
        origin: params.origin,
        destination: params.destination,
        dhoRadius: String(params.dhoRadius),
        dhdRadius: String(params.dhdRadius),
        equipmentTypes: params.equipmentTypes,
        loadType: params.loadType === "both" ? "both" : params.loadType,
        minLength: params.minLength ? String(params.minLength) : "",
        minWeight: params.minWeight ? String(params.minWeight) : "",
        pickupDate: params.pickupDateFrom,
        hideCanada: params.hideCanada,
        searchBack: String(params.searchBack),
        sortBy: (tabsRef.current.find((t) => t.id === activeTabIdRef.current))?.filters.sortBy ?? "age_newest",
      };
      const label = buildLabel(newFilters);

      // Build the LoadSearchParams for realtime matching reuse
      const searchParams: LoadSearchParams = {
        origin: params.origin,
        destination: params.destination,
        equipmentTypes: params.equipmentTypes,
        loadType: params.loadType as 'full' | 'partial' | 'both',
        hideCanada: params.hideCanada,
        searchBack: params.searchBack,
        dhoRadius: params.dhoRadius,
        dhdRadius: params.dhdRadius,
      };

      // If no tabs exist, auto-create the first tab
      // Read current tab from ref to avoid stale closure
      const currentTab = tabsRef.current.find((t) => t.id === activeTabIdRef.current);
      let targetTabId: string;
      if (!currentTab) {
        const newTab: SearchTab = {
          id: crypto.randomUUID(),
          label,
          filters: newFilters,
          hasSearched: true,
        };
        targetTabId = newTab.id;
        addTabWithOverflow(newTab);
      } else {
        targetTabId = currentTab.id;
        updateTab(currentTab.id, { filters: newFilters, label, hasSearched: true });
      }

      // ── Per-tab generation counter: cancel any prior search for THIS tab ──
      const prevGen = searchGenByTab.current.get(targetTabId) ?? 0;
      const gen = prevGen + 1;
      searchGenByTab.current.set(targetTabId, gen);

      // ── Abort prior in-flight request for this tab ──
      const prevAbort = abortByTab.current.get(targetTabId);
      if (prevAbort) prevAbort.abort();
      const abortController = new AbortController();
      abortByTab.current.set(targetTabId, abortController);

      // Mark this tab as loading — and clear auto-search guard so future
      // manual searches with changed params are never blocked
      autoSearchedTabs.current.delete(targetTabId);
      if (isBackground) {
        setRefreshingTabs(prev => ({ ...prev, [targetTabId]: true }));
      } else {
        // Foreground supersedes any in-flight background refresh indicator
        setRefreshingTabs(prev => ({ ...prev, [targetTabId]: false }));
        setTabSearching(targetTabId, true);
      }

      // ── Timeout guard: auto-abort after 30s ──
      const timeout = setTimeout(() => abortController.abort(), 30_000);

      try {
        // Check abort before each async step
        if (abortController.signal.aborted) throw new DOMException("Aborted", "AbortError");

        // ── Single geocode pass ──
        const [originCoords, destCoords] = await Promise.all([
          params.origin ? geocodeLocation(params.origin) : null,
          params.destination ? geocodeLocation(params.destination) : null,
        ]);

        if (abortController.signal.aborted) throw new DOMException("Aborted", "AbortError");

        // Store coords immediately for realtime matching
        coordsCache.current.set(targetTabId, { originCoords, destCoords });

        // ── Run primary DB query AND similar query in parallel ──
        // Similar starts immediately with current searchParams; we apply the
        // exact-id filter + DAT-style subtype fallback decision after primary
        // returns, so behavior is preserved while we save the serial wait.
        setTabSimilarLoading(targetTabId, true);
        const similarPromise: Promise<DatabaseLoad[]> = (async () => {
          try {
            return await searchSimilarLoads(searchParams, originCoords, destCoords, new Set<string>(), isDemoUser);
          } catch {
            return [];
          }
        })();

        const results = await searchLoads(searchParams, originCoords, destCoords, isDemoUser);

        if (abortController.signal.aborted) throw new DOMException("Aborted", "AbortError");

        const filtered = applyClientFilters(results, {
          minWeight: params.minWeight,
          minLength: params.minLength,
          pickupDateFrom: params.pickupDateFrom,
          pickupDateTo: params.pickupDateTo,
        });

        // Capture prior result ids BEFORE overwriting cache (for unseen delta on background refresh)
        const prevResults = resultsCache.current.get(targetTabId);
        const prevIds = prevResults ? new Set(prevResults.map(l => l.id)) : null;

        resultsCache.current.set(targetTabId, filtered);
        searchParamsCache.current.set(targetTabId, searchParams);
        // Mark this tab fresh ONLY on a successful results write
        lastFetchedAt.current.set(targetTabId, Date.now());

        // ── Background refresh: increment unseen count for new ids if tab is inactive ──
        if (isBackground && prevIds && targetTabId !== activeTabIdRef.current) {
          let added = 0;
          for (const l of filtered) {
            if (!prevIds.has(l.id) && markUnseenSeen(targetTabId, l.id)) added += 1;
          }
          if (added > 0) {
            setTabUnseen(prev => ({ ...prev, [targetTabId]: (prev[targetTabId] ?? 0) + added }));
          }
        }

        // Clear loading for THIS tab — results are ready
        if (isBackground) {
          setRefreshingTabs(prev => ({ ...prev, [targetTabId]: false }));
        } else {
          setTabSearching(targetTabId, false);
        }

        // Clear any stale fading/new state
        setFadingLoads(new Map());
        setNewLoadIds(new Set());

        // ── Resolve the in-flight similar query, apply exact-id filter, and
        //    handle the DAT-style subtype fallback when primary is empty.
        const exactIds = new Set(filtered.map(l => l.id));
        (async () => {
          try {
            let similar = await similarPromise;
            // Drop loads that are already in the exact result set
            similar = similar.filter(l => !exactIds.has(l.id));

            // Subtype fallback: if primary empty AND user picked a specific subtype,
            // re-run similar with broadened equipment family (rare path).
            if (
              filtered.length === 0 &&
              hasSpecificSubtype(searchParams.equipmentTypes) &&
              similar.length === 0
            ) {
              const fallbackParams = {
                ...searchParams,
                equipmentTypes: expandEquipmentTypesForFallback(searchParams.equipmentTypes),
              };
              similar = await searchSimilarLoads(fallbackParams, originCoords, destCoords, exactIds, isDemoUser);
            }

            // ── Layer 2 / Layer 3 sequential cascade ──
            const layerSources: Array<'layer1' | 'layer2' | 'layer3'> = [];
            if (similar.length > 0) layerSources.push('layer1');

            const originState = parseStateFromInput(searchParams.origin);
            const destState = parseStateFromInput(searchParams.destination);
            const stateLayersEligible =
              !!originState &&
              !!destState &&
              !!searchParams.destination?.trim();

            if (filtered.length === 0 && similar.length < 3 && stateLayersEligible) {
              const excludeAfterL1 = new Set<string>([
                ...exactIds,
                ...similar.map(l => l.id),
              ]);
              const layer2 = await searchStateToStateSameEquipment(
                searchParams, originState!, destState!, excludeAfterL1, isDemoUser,
              );
              if (layer2.length > 0) {
                similar = [...similar, ...layer2];
                layerSources.push('layer2');
              }

              if (filtered.length === 0 && (similar.length) < 3) {
                const excludeAfterL2 = new Set<string>([
                  ...excludeAfterL1,
                  ...layer2.map(l => l.id),
                ]);
                const layer3 = await searchStateToStateAnyEquipment(
                  searchParams, originState!, destState!, excludeAfterL2, isDemoUser,
                );
                if (layer3.length > 0) {
                  similar = [...similar, ...layer3];
                  layerSources.push('layer3');
                }
              }
            }

            // Only write if this is still the latest search for this tab
            if (searchGenByTab.current.get(targetTabId) === gen) {
              similarResultsCache.current.set(targetTabId, similar);
              similarCountCache.current.set(targetTabId, similar.length);
              similarMetaCache.current.set(targetTabId, {
                sources: layerSources,
                originState,
                destState,
              });
            }
          } catch {
            if (searchGenByTab.current.get(targetTabId) === gen) {
              similarResultsCache.current.set(targetTabId, []);
              similarCountCache.current.set(targetTabId, 0);
              similarMetaCache.current.set(targetTabId, { sources: [], originState: null, destState: null });
            }
          } finally {
            if (searchGenByTab.current.get(targetTabId) === gen) {
              setTabSimilarLoading(targetTabId, false);
            }
          }
        })();

      } catch (err) {
        // Silently ignore aborted requests — they are intentional cancellations
        if (err instanceof DOMException && err.name === "AbortError") {
          const currentGen = searchGenByTab.current.get(targetTabId) ?? 0;
          if (currentGen > gen) {
            // A newer search IS responsible — don't clear loading
            return;
          }
          // Timeout abort — no newer search to clear loading. Clear it ourselves.
          // Do NOT update lastFetchedAt — tab is not considered fresh on abort.
          if (isBackground) {
            setRefreshingTabs(prev => ({ ...prev, [targetTabId]: false }));
          } else {
            setTabSearching(targetTabId, false);
          }
          return;
        }
        console.error("Search error:", err);
        // On hard failure: don't overwrite cached results in background mode
        // (keep the user's last good results visible). Foreground keeps prior behavior.
        if (isBackground) {
          setRefreshingTabs(prev => ({ ...prev, [targetTabId]: false }));
        } else {
          resultsCache.current.set(targetTabId, []);
          setTabSearching(targetTabId, false);
        }
      } finally {
        clearTimeout(timeout);
        // Clean up abort controller if it's still the current one for this tab
        if (abortByTab.current.get(targetTabId) === abortController) {
          abortByTab.current.delete(targetTabId);
        }
      }
    },
    [updateTab, addTabWithOverflow, setTabSearching, setTabSimilarLoading],
  );

  // ── Tab actions ──
  const handleAddTab = useCallback(() => {
    const newTab = createBlankTab();
    addTabWithOverflow(newTab);
    setFadingLoads(new Map());
    setNewLoadIds(new Set());
  }, [addTabWithOverflow]);

  const handleCloseTab = useCallback(
    (tabId: string) => {
      // Cancel any in-flight search for this tab
      const abort = abortByTab.current.get(tabId);
      if (abort) abort.abort();
      abortByTab.current.delete(tabId);
      searchGenByTab.current.delete(tabId);
      autoSearchedTabs.current.delete(tabId);
      lastFetchedAt.current.delete(tabId);
      unseenSeenIds.current.delete(tabId);
      setTabUnseen(prev => { if (!(tabId in prev)) return prev; const n = { ...prev }; delete n[tabId]; return n; });
      setFormCollapsedMap(prev => { if (!(tabId in prev)) return prev; const n = { ...prev }; delete n[tabId]; return n; });
      // Clean per-tab loading state
      setSearchingTabs(prev => { const n = { ...prev }; delete n[tabId]; return n; });
      setSimilarLoadingTabs(prev => { const n = { ...prev }; delete n[tabId]; return n; });
      setRefreshingTabs(prev => { const n = { ...prev }; delete n[tabId]; return n; });

      if (tabs.length <= 1) {
        resultsCache.current.delete(tabId);
        coordsCache.current.delete(tabId);
        searchParamsCache.current.delete(tabId);
        similarResultsCache.current.delete(tabId);
        similarCountCache.current.delete(tabId);
        similarMetaCache.current.delete(tabId);
        pendingFiltersRef.current = { ...DEFAULT_FILTERS, equipmentTypes: [] };
        setTabs([]);
        setActiveTabId("");
        setFadingLoads(new Map());
        setNewLoadIds(new Set());
        return;
      }
      const idx = tabs.findIndex((t) => t.id === tabId);
      const newTabs = tabs.filter((t) => t.id !== tabId);
      resultsCache.current.delete(tabId);
      coordsCache.current.delete(tabId);
      searchParamsCache.current.delete(tabId);
      similarResultsCache.current.delete(tabId);
      similarCountCache.current.delete(tabId);
      similarMetaCache.current.delete(tabId);

      if (activeTabId === tabId) {
        const newActive = newTabs[Math.max(0, idx - 1)];
        setActiveTabId(newActive.id);
      }
      setTabs(newTabs);
      setFadingLoads(new Map());
      setNewLoadIds(new Set());
    },
    [tabs, activeTabId],
  );

  const handleSelectTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
    setFadingLoads(new Map());
    setNewLoadIds(new Set());
    clearAlerted();
    setTabUnseen(prev => { if (!prev[tabId]) return prev; const n = { ...prev }; delete n[tabId]; return n; });
  }, [clearAlerted]);

  const handleToggleAlert = useCallback((tabId: string) => {
    setTabs(prev => prev.map(t => {
      if (t.id !== tabId) return t;
      const next = !t.alertEnabled;
      if (next) requestPermission();
      return { ...t, alertEnabled: next };
    }));
  }, [requestPermission]);

  // ── Safety reconciliation: clear stuck loading if results already cached ──
  useEffect(() => {
    if (!activeTabId || !searchingTabs[activeTabId]) return;
    if (!resultsCache.current.has(activeTabId)) return;
    const timer = setTimeout(() => {
      setSearchingTabs(prev => {
        if (!prev[activeTabId]) return prev;
        return { ...prev, [activeTabId]: false };
      });
    }, 3000);
    return () => clearTimeout(timer);
  }, [activeTabId, searchingTabs]);

  // ── Derive display state ──
  const displayLoads = cachedResults ?? [];
  const hasSearched = activeTab?.hasSearched ?? false;
  const currentSortBy = activeTab?.filters.sortBy ?? "age_newest";
  const similarLoads = activeTab ? similarResultsCache.current.get(activeTab.id) ?? [] : [];
  const similarCount = activeTab ? similarCountCache.current.get(activeTab.id) ?? 0 : 0;
  const similarMeta = activeTab ? similarMetaCache.current.get(activeTab.id) ?? null : null;

  const handleSortChange = useCallback((sort: SortOption) => {
    if (!activeTab) return;
    updateTab(activeTab.id, {
      filters: { ...activeTab.filters, sortBy: sort },
    });
  }, [activeTab, updateTab]);

  // ── Boosted loads ──
  const { boostedLoads } = useBoostedLoads({
    enabled: hasSearched && displayLoads.length > 0,
    equipmentTypes: activeTab?.filters.equipmentTypes ?? [],
    originCoords: activeCoords?.originCoords ?? null,
    destCoords: activeCoords?.destCoords ?? null,
    originRadius: clampRadiusValue(activeTab?.filters.dhoRadius, 50),
    destRadius: clampRadiusValue(activeTab?.filters.dhdRadius, 50),
    hideCanada: activeTab?.filters.hideCanada ?? false,
  });

  // ── Scroll-to boosted load ──
  const [scrollToLoadId, setScrollToLoadId] = useState<string | null>(null);
  const [externalExpandedLoadId, setExternalExpandedLoadId] = useState<string | null>(null);

  const handleSelectBoostedLoad = useCallback((loadId: string) => {
    setExternalExpandedLoadId(loadId);
    setScrollToLoadId(loadId);
    setTimeout(() => setScrollToLoadId(null), 100);
  }, []);

  if (showMyOffers) {
    return (
      <div className="flex flex-col w-full h-full">
        <div className="flex items-center gap-3 border-b px-4 py-2.5">
          <button
            onClick={() => setSearchParams({}, { replace: true })}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back to Search
          </button>
          <h2 className="text-sm font-bold text-foreground">My Offers</h2>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <CarrierOffersSection />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full">
      {/* ═══ Active Search Tabs ═══ */}
      <ActiveSearchTabs
        tabs={tabs}
        activeTabId={activeTabId}
        onSelectTab={handleSelectTab}
        onAddTab={handleAddTab}
        onCloseTab={handleCloseTab}
        onToggleAlert={handleToggleAlert}
        unseenCounts={tabUnseen}
      />

      {/* ═══ Mobile collapsed summary (mobile only, when collapsed) ═══ */}
      {(() => {
        const isFormCollapsed = activeTabId
          ? (formCollapsedMap[activeTabId] ?? !!activeTab?.hasSearched)
          : false;
        return (
          <>
            {isFormCollapsed && (
              <MobileSearchSummary
                origin={currentFilters.origin}
                destination={currentFilters.destination}
                equipmentTypes={currentFilters.equipmentTypes}
                loadType={currentFilters.loadType}
                onEdit={() => {
                  if (activeTabId) {
                    setFormCollapsedMap(prev => ({ ...prev, [activeTabId]: false }));
                  }
                }}
              />
            )}

            {/* ═══ Filter Bar — hidden on mobile when collapsed; always shown on desktop ═══ */}
            <div className={isFormCollapsed ? "hidden md:block" : ""}>
              <SearchFilterBar
                key={filterBarKey}
                onSearch={(params) => {
                  if (activeTabId) {
                    setFormCollapsedMap(prev => ({ ...prev, [activeTabId]: true }));
                  }
                  handleSearch(params);
                }}
                isSearching={isSearching}
                isRefreshing={isRefreshing}
                resultCount={hasSearched ? displayLoads.length : undefined}
                filterValues={currentFilters}
                onFilterChange={handleFilterChange}
              />
            </div>
          </>
        );
      })()}
      {/* ═══ Results (contained scroll area) ═══ */}
      {hasSearched && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <FeaturedLoadsRail
            boostedLoads={boostedLoads}
            onSelectLoad={handleSelectBoostedLoad}
          />
          <SearchResultsTable
            loads={displayLoads}
            isLoading={isSearching}
            fadingLoadIds={fadingLoads}
            newLoadIds={newLoadIds}
            searchOriginCoords={activeCoords?.originCoords ?? null}
            searchDestCoords={activeCoords?.destCoords ?? null}
            sortBy={currentSortBy}
            onSortChange={handleSortChange}
            scrollToLoadId={scrollToLoadId}
            externalExpandedLoadId={externalExpandedLoadId}
            onExternalExpandedLoadChange={setExternalExpandedLoadId}
          />
          <SimilarResultsSection
            similarLoads={similarLoads}
            count={similarCount}
            isLoading={isSimilarLoading}
            searchOriginCoords={activeCoords?.originCoords ?? null}
            searchDestCoords={activeCoords?.destCoords ?? null}
            sortBy={currentSortBy}
            onSortChange={handleSortChange}
            meta={similarMeta}
          />
        </div>
      )}
      {!hasSearched && (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-2">
          <span className="text-sm">
            Enter search criteria and click Search Loads to find available freight.
          </span>
        </div>
      )}
    </div>
  );
}
